import { skipToken } from '@reduxjs/toolkit/query'
import { useCallback, useEffect, useMemo } from 'react'
import { routes } from '@/routes'
import { filterNullish } from '@sevenrooms/api'
import type { Upgrade } from '@sevenrooms/domain'
import { ReservationWidget as ReservationWidgetConst } from '@sevenrooms/domain/constants'
import { FormatService, type CountryCode, type LanguageCode } from '@sevenrooms/locales'
import { Surface, useNavigation } from '@sevenrooms/navigation'
import { EmbeddedMap } from '@sevenrooms/ui-kit/core'
import { useMaxWidthBreakpoint } from '@sevenrooms/ui-kit/hooks'
import { VStack, Window, Box } from '@sevenrooms/ui-kit/layout'
import {
  ManageGuestPreferences,
  CancelModal,
  UnableModifyModal,
  UnableAddUpgradesModal,
  ReservationDetailCard,
  VenueDetails,
  WidgetPortal,
  PrivateEventsSummary,
  MarketingOptInModal,
} from '../../components'
import { useVenue, useManageReservationRoute, useWidgetSettings, useClientLogin } from '../../hooks'
import { useGetManageReservationDetailsQuery, useLazyGetManageReservationDetailsQuery, useModals } from '../../store'
import { calculateCategoryFees } from '../../utils/calculateFees'
import { ActionPanel } from './ActionPanel'
import { AdditionalUpgradesCard } from './AdditionalUpgradesCard'
import { AddUpgradesModal } from './AddUpgradesModal'
import { ManageReservationHeader, type ManageReservationHeaderProps } from './ManageReservationHeader'
import { ManageReservationSkeleton } from './ManageReservationSkeleton'
import { PaymentDetails } from './PaymentDetails'
import { PolicyDetails } from './PolicyDetails'
import { UpgradeDetails } from './UpgradeDetails'
import { UpgradesBanner } from './UpgradesBanner'

const managePath = routes.reservations.manage

export function Manage() {
  const widgetSettings = useWidgetSettings()
  const venue = useVenue()
  const {
    id: venueId,
    name,
    urlKey,
    currencyCode,
    sendConfirmationsResWidgetBookings,
    gmapsLinkByPlaceId,
    defaultServiceCharge,
    defaultGratuity,
    includeEndTime,
    includeSeatingArea,
    gmapsEmbedUrl,
    includeSpecialAttentionMessage,
    showCancelLink,
    isPrivateEventsSettingsEnabled,
  } = venue
  const { closeSurfaceHref, push } = useNavigation()
  const { showErrorModal } = useModals()
  const { lang, token, venueKey, isConfirm, isSuccess, isSuccessEdit, marketingOptIn, linkToModifyReservation, linkToPurchaseUpgrades } =
    useManageReservationRoute()
  const isMobile = useMaxWidthBreakpoint('s')
  const {
    userInfo: { clientId: sevenroomsClientId, profilePictureUrl },
  } = useClientLogin()
  const preferredLanguageLocalStorage = useMemo(() => {
    const prefLang = window.localStorage.getItem(ReservationWidgetConst.PreferredLanguageCookie)
    if (prefLang) {
      return JSON.parse(prefLang) as LanguageCode
    }
    return undefined
  }, [])

  const {
    isError: isGetDetailsError,
    isLoading,
    data,
  } = useGetManageReservationDetailsQuery(token ? { lang: preferredLanguageLocalStorage || lang, token } : skipToken)
  const [getManageReservationDetails, { isFetching: isReFetchingDetails }] = useLazyGetManageReservationDetailsQuery()
  const {
    actual,
    otherBundleActuals,
    bookingAccess,
    charges = [],
    calendarLinks,
    policies,
    actualFlags,
    venueTags,
    additionalUpgrades,
    upsell,
    experience,
  } = data ?? {}

  const isPrivateEventsExperience = !!experience && experience.offerType === 'PDR'

  const minSpend = actual?.minPrice

  const guestContact = getGuestContact(
    actual?.lastConfirmationMethod,
    actual?.email,
    actual?.phoneNumber,
    actual?.phoneNumberLocale as CountryCode
  )

  const isError = isGetDetailsError || !venueKey || !token
  const hasAdditionalUpgrades = !!additionalUpgrades?.data.length

  const onModifyBtnClick = useCallback(async () => {
    try {
      if (data?.actualFlags?.isEditable) {
        window.open(linkToModifyReservation, '_blank')
      } else {
        push(managePath.modifyModal, {
          params: { venueKey: urlKey },
        })
      }
    } catch {
      showErrorModal()
    }
  }, [data?.actualFlags?.isEditable, linkToModifyReservation, push, showErrorModal, urlKey])

  const onCancelBtnClick = useCallback(async () => {
    try {
      await getManageReservationDetails({ token: token as string, lang }).unwrap()
      push(managePath.cancelModal, {
        params: { venueKey: urlKey },
      })
    } catch {
      showErrorModal()
    }
  }, [getManageReservationDetails, lang, push, showErrorModal, token, urlKey])

  const onAddUpgradesBtnClick = useCallback(async () => {
    window.open(linkToPurchaseUpgrades, '_blank')
  }, [linkToPurchaseUpgrades])

  const onMapClick = useMemo(() => {
    if (!gmapsLinkByPlaceId) {
      // we return undefined here, so that the EmbeddedMap component doesn't render the click wrapper
      return undefined
    }
    const clickFunc = () => {
      window.open(gmapsLinkByPlaceId, '_blank')
    }
    return clickFunc
  }, [gmapsLinkByPlaceId])

  useEffect(() => {
    if (isError) {
      showErrorModal()
    }
  }, [isError, showErrorModal])

  if (isLoading || isError) {
    return <ManageReservationSkeleton />
  }
  const imageUrl = `/.h/download/${
    widgetSettings.headerImgPhotoDict?.mega ?? widgetSettings.headerImgPhotoDict?.medium ?? widgetSettings.headerImgPhotoDict?.large
  }`
  let flowType: ManageReservationHeaderProps['flowType']
  if (!hasAdditionalUpgrades && isConfirm) {
    flowType = 'confirmation'
  } else if (isSuccess) {
    flowType = 'create'
  } else if (isSuccessEdit) {
    flowType = 'update'
  }

  const upgrades: Upgrade[] = (actual?.selectedUpsellsInformation ?? [])
    .map(upgrade => {
      if (!widgetSettings.isFeesInPriceDisplayed) {
        return upgrade
      }

      if (!upsell) {
        return undefined
      }

      const { categories, inventories } = upsell
      const upgradeInventory = inventories.find(inventory => inventory.id === upgrade.id)
      if (!upgradeInventory) {
        return undefined
      }

      const upgradeCategory = categories.find(category => category.id === upgradeInventory.categoryId)
      if (!upgradeCategory) {
        return undefined
      }

      return {
        ...upgrade,
        fees: calculateCategoryFees(upgrade.price, upgradeCategory, defaultServiceCharge, defaultGratuity),
      }
    })
    .filter(filterNullish)

  const guestCount =
    actual &&
    actual.maxGuests +
      (Array.isArray(otherBundleActuals)
        ? otherBundleActuals.reduce((acc: number, { maxGuests }: { maxGuests: number }) => acc + maxGuests, 0)
        : 0)
  const reservationReferenceIds = actual && [
    actual.referenceCode,
    ...(otherBundleActuals ?? []).map(({ referenceCode }: { referenceCode: string }) => referenceCode),
  ]

  const events = []
  if (actual && actual.eventName) {
    events.push({
      eventName: actual.eventName,
      eventQuantity: actual.eventQuantity,
      minPrice: actual.minPrice,
    })
  }
  if (Array.isArray(otherBundleActuals)) {
    otherBundleActuals.forEach(({ eventName, eventQuantity, minPrice }) => {
      if (eventName) {
        events.push({ eventName, eventQuantity, minPrice })
      }
    })
  }

  return (
    <>
      <VStack width="100%" maxWidth={isMobile ? '100%' : '428px'} spacing="lm">
        {isMobile && (
          <ManageReservationHeader
            flowType={flowType}
            guestContact={guestContact}
            alignItems="center"
            flexDirection="column"
            bookingConfirmationEnabled={sendConfirmationsResWidgetBookings}
          />
        )}
        <ReservationDetailCard
          calendarLinks={calendarLinks}
          imageUrl={imageUrl}
          venueName={name}
          timeSlotDescription={bookingAccess?.timeSlotDescription}
          guestCount={guestCount}
          maleCount={actual?.mfRatioMale}
          femaleCount={actual?.mfRatioFemale}
          guestDisplayName={actual?.clientDisplayNameWithSalutation}
          reservationDate={actual?.date}
          reservationTime={actual?.arrivalTime}
          reservationDuration={includeEndTime ? actual?.duration : undefined}
          // minPrice is undefined in case of minPriceFormatted is NO_MIN or COMP, we hide row for these values
          reservationMinimum={actual?.minPrice && isPrivateEventsExperience && events.length === 0 ? actual?.minPriceFormatted : undefined}
          offerName={experience?.name}
          reservationReferenceIds={reservationReferenceIds}
          seatingAreaDescription={includeSeatingArea ? actual?.venueSeatingAreaName : undefined}
          events={events.length > 0 ? events : undefined}
          fallback={
            gmapsEmbedUrl ? (
              <EmbeddedMap clickTestId="venue-map" embeddedMapUrl={gmapsEmbedUrl} height="240px" width="100%" onMapClick={onMapClick} />
            ) : (
              <></>
            )
          }
        />
      </VStack>
      <VStack width="100%">
        {!isMobile && (
          <Box mb="lm">
            <ManageReservationHeader
              flowType={flowType}
              guestContact={guestContact}
              alignItems="left"
              bookingConfirmationEnabled={sendConfirmationsResWidgetBookings}
            />
          </Box>
        )}
        <VStack spacing="l">
          <ManageGuestPreferences
            actualId={actual?.id}
            guestCount={guestCount}
            venueTags={venueTags}
            clientId={actual?.venueGroupClientId}
            venueId={venueId}
            clientPhoto={actual?.clientPhoto}
            clientDisplayName={actual?.clientDisplayName}
            showDietaryRestrictions={widgetSettings.enableDietaryRestrictionsConfirmation}
            showCustomQuestion={widgetSettings.enableCustomQuestionConfirmation}
            showGuestImageInput={widgetSettings.enableProfilePhotoConfirmation && !profilePictureUrl}
            showSpecialOccasions={widgetSettings.enableSpecialOccasionsConfirmation}
            requireGdprOptin={widgetSettings.requireDietaryTagGdprOptInConfirmation}
            champagnePreferenceTags={actual?.champagnePreferenceTags}
            clientDietaryPreferenceTags={actual?.clientDietaryPreferenceTags}
            liquorPreferenceTags={actual?.liquorPreferenceTags}
            partyDietaryPreferenceTags={actual?.partyDietaryPreferenceTags}
            specialOccasionTags={actual?.specialOccasionTags}
            otherInfoText={actual?.customQuestionResponseNote}
            agreedToGdprDietaryOptin={actual?.agreedToGdprDietaryOptin}
            sevenroomsClientId={sevenroomsClientId}
            onError={showErrorModal}
          />
          <PaymentDetails charges={charges} lang={lang} currencyCode={currencyCode} />
          <UpgradeDetails upgrades={upgrades} currencyCode={venue.currencyCode} />
          {isPrivateEventsExperience && (
            <PrivateEventsSummary
              minSpend={minSpend ?? undefined}
              fees={actual?.pdrFees ?? undefined}
              currencyCode={venue.currencyCode}
              showPrivateEventsPolicy={!isPrivateEventsSettingsEnabled}
              cancellationPolicy={policies?.cancellationPolicy}
              bookingCheckoutPolicy={policies?.bookingCheckoutPolicy}
            />
          )}
          {hasAdditionalUpgrades && (
            <AdditionalUpgradesCard
              upgrades={additionalUpgrades?.data}
              hasMore={additionalUpgrades.hasMoreUpgrades}
              onAdd={onAddUpgradesBtnClick}
              currencyCode={currencyCode}
            />
          )}
          <VStack spacing="lm">
            <PolicyDetails
              dataTestId="reservation-widget-policy-details"
              showSpecialAttentionMessage={includeSpecialAttentionMessage}
              {...(!isPrivateEventsExperience ? { ...policies } : {})}
            />
            <ActionPanel
              isEditable={showCancelLink && actualFlags?.isEditable}
              isCancelable={showCancelLink && actualFlags?.isCancelable}
              onModifyBtnClick={onModifyBtnClick}
              onCancelBtnClick={onCancelBtnClick}
              disabled={isReFetchingDetails}
            />
          </VStack>
          <VenueDetails
            venue={venue}
            // need to hide map in venue details if we have it in reservation details card
            gmapsEmbedUrl={widgetSettings.headerImgUrl ? gmapsEmbedUrl : undefined}
          />
        </VStack>
      </VStack>
      {widgetSettings.venueSpecificMarketingOn &&
        (widgetSettings.showEmailMarketingOptInModalOnConfirmationPage ?? true) &&
        !marketingOptIn && (
          <Box position="fixed" bottom={isMobile ? 'none' : 's'} left={isMobile ? 'none' : undefined} right={isMobile ? 'none' : 's'}>
            <MarketingOptInModal isMobile={isMobile} brandColor={widgetSettings.primaryColor} clientId={actual?.venueGroupClientId} />
          </Box>
        )}
      {!!additionalUpgrades?.data.length && (
        <>
          <Surface destination={managePath.addUpgradesModal}>
            <Window>
              <AddUpgradesModal
                upgrades={additionalUpgrades.data}
                onConfirm={onAddUpgradesBtnClick}
                closeHref={closeSurfaceHref(managePath.addUpgradesModal, {
                  params: { venueKey: urlKey },
                })}
                currencyCode={currencyCode}
              />
            </Window>
          </Surface>
          <WidgetPortal portalId="manage-res-portal">
            <Box mb="lm">
              <UpgradesBanner onConfirm={onAddUpgradesBtnClick} />
            </Box>
          </WidgetPortal>
        </>
      )}
      <Surface destination={managePath.modifyModal}>
        <Window>
          <UnableModifyModal
            closeHref={closeSurfaceHref(managePath.modifyModal, {
              params: { venueKey: urlKey },
            })}
          />
        </Window>
      </Surface>
      <Surface destination={managePath.cancelModal}>
        <Window>
          <CancelModal
            closeHref={closeSurfaceHref(managePath.cancelModal, {
              params: { venueKey: urlKey },
            })}
          />
        </Window>
      </Surface>
      <Surface destination={managePath.errorUpgrades}>
        <Window>
          <UnableAddUpgradesModal
            closeHref={closeSurfaceHref(managePath.errorUpgrades, {
              params: { venueKey: urlKey },
            })}
          />
        </Window>
      </Surface>
    </>
  )
}

const getGuestContact = (
  lastConfirmationMethod?: string | null,
  email?: string | null,
  phoneNumber?: string | null,
  phoneNumberLocale?: CountryCode | null
) => {
  if (lastConfirmationMethod === 'SMS' && phoneNumber && phoneNumberLocale) {
    return FormatService.formatPhoneNumber(phoneNumber, phoneNumberLocale)
  }
  return email
}
